import {
    AppModule,
    PrivilegedRouteMap,
    PrivilegedRouteObject,
} from '@thoughtspot/blink-context';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import React from 'react';
import { lazyWithPreload as lazy } from 'react-lazy-with-preload';
import { Outlet } from 'react-router-dom';
import { BlinkV2MetricsContextStates } from '/@services/system/blink-v2-metrics-context-states';
import { AUTHENTICATED_CORE_ROUTES, CORE_ROUTES } from './core-routes';
import { INSIGHTS_ROUTES } from './insights-routes';
import { PrivilegeList } from './privilege-list';

export enum Applications {
    INSIGHTS = 'insights',
    DATA = 'data',
    TEAMS_ADMIN = 'teams',
    ADMIN = 'admin',
    DEVELOP = 'develop',
    CORE = 'core',
    ANALYST_STUDIO = 'AnalystStudio',
}

export type Application = Omit<PrivilegedRouteObject, 'handle'> & {
    appName?: Applications;
    handle: string;
    homePath?: string;
    description?: string;
    icon?: IconID;
    shouldRedirectToV1?: boolean;
    titleIconId?: IconID;
};

const DataModelPage = lazy(() => import('../pages/data-management/index'));
const SchemaViewerPage = lazy(() => import('../pages/schema-viewer/index'));
const SpotAppExportPage = lazy(() => import('../pages/spotapp-export/index'));
const AutoWorksheetPage = lazy(() =>
    import('../pages/worksheet-pages/auto-worksheet-page'),
);

const WorksheetActions = lazy(() =>
    import('../pages/worksheet-actions/worksheet-actions'),
);

const WorksheetV2 = lazy(() => import('../components/worksheet-v2'));

export const DATA_ROUTES: PrivilegedRouteMap = {
    TmlExportPage: {
        path: '/data/spotapp-export',
        element: <SpotAppExportPage />,
        disableTopNav: true,
        pageContext: BlinkV2MetricsContextStates.DATA_MANAGEMENT,
        appModule: AppModule.Data,
        isOverlayLeftPanel: true,
    },
    AutoWorksheetPage: {
        path: '/data/worksheet/create/auto',
        element: <AutoWorksheetPage />,
        disableTopNav: true,
        pageContext: BlinkV2MetricsContextStates.ONBOARDING,
        appModule: AppModule.Data,
        isOverlayLeftPanel: true,
    },
    DataModelPage: {
        path: '/data/*',
        element: <DataModelPage />,
        pageContext: BlinkV2MetricsContextStates.DATA_MANAGEMENT,
        appModule: AppModule.Data,
    },
    WorksheetAssociation: {
        path: '/worksheetActions/:worksheetId',
        element: <WorksheetActions />,
        pageContext: BlinkV2MetricsContextStates.WORKSHEET_ACTIONS,
        appModule: AppModule.Data,
    },
    SchemaViewer: {
        path: '/schema-viewer/table',
        element: <SchemaViewerPage />,
        pageContext: BlinkV2MetricsContextStates.DATA_MANAGEMENT,
        appModule: AppModule.Data,
        privileges: [PrivilegeList.CanManageWorksheetViewsTables],
        isOverlayLeftPanel: true,
    },
    WorksheetV2: {
        path: '/worksheet/*',
        element: <WorksheetV2 />,
        disableTopNav: true,
        pageContext: BlinkV2MetricsContextStates.WORKSHEET,
        appModule: AppModule.Data,
        isOverlayLeftPanel: true,
    },
};

const EverywherePage = lazy(() =>
    import('../pages/embed-dev-portal/everywhere'),
);
const EverywhereStandalonePage = lazy(() =>
    import('../pages/embed-dev-portal/everywhere-standalone'),
);

export const DEVELOP_ROUTES: PrivilegedRouteMap = {
    EverywhereStandalonePage: {
        path: '/everywhere/*',
        element: <EverywhereStandalonePage />,
        disableTopNav: true,
        pageContext: BlinkV2MetricsContextStates.EVERYWHERE_PAGE,
        appModule: AppModule.Develop,
    },
    EverywherePage: {
        path: '/develop/*',
        element: <EverywherePage />,
        pageContext: BlinkV2MetricsContextStates.EVERYWHERE_PAGE,
        appModule: AppModule.Develop,
    },
};

const TeamsPage = lazy(() => import('../pages/teams/teams-page'));

export const TEAMS_ROUTES: PrivilegedRouteMap = {
    TeamsPage: {
        path: '/teams/*',
        element: <TeamsPage />,
        pageContext: BlinkV2MetricsContextStates.TEAMS,
    },
};

const AdminPage = lazy(() => import('../pages/admin/admin-page'));

export const ADMIN_ROUTES: PrivilegedRouteMap = {
    AdminPage: {
        path: '/admin',
        element: <AdminPage />,
        pageContext: BlinkV2MetricsContextStates.ADMIN_PAGE,
        appModule: AppModule.Admin,
    },
};

export const ANALYST_STUDIO_ROUTES: PrivilegedRouteMap = {};

export const ROUTES = {
    ...ADMIN_ROUTES,
    ...CORE_ROUTES,
    ...DATA_ROUTES,
    ...DEVELOP_ROUTES,
    ...INSIGHTS_ROUTES,
    ...TEAMS_ROUTES,
    ...ANALYST_STUDIO_ROUTES,
};

export const ROUTE_IDS: any = Object.keys(ROUTES).reduce((acc: any, key) => {
    acc[key] = key;
    return acc;
}, {});

export const ApplicationList: { [key in Applications]?: Application } = {
    [Applications.INSIGHTS]: {
        children: INSIGHTS_ROUTES,
        description: 'gnav.appSwitcher.description.THOUGHTSPOT_ONE',
        element: <Outlet />,
        handle: Applications.INSIGHTS,
        homePath: '/insights/home',
        icon: IconID.MAGNIFYING_GLASS,
        id: Applications.INSIGHTS,
        title: 'gnav.appSwitcher.THOUGHTSPOT_ONE',
        privileges: [],
        pageContext: BlinkV2MetricsContextStates.DEFAULT_PAGE,
        appModule: AppModule.Insights,
    },
    [Applications.DATA]: {
        children: DATA_ROUTES,
        description: 'gnav.appSwitcher.description.DATA',
        element: <Outlet />,
        handle: Applications.DATA,
        homePath: '/data/tables',
        icon: IconID.SAVE_WORKSHEET,
        id: Applications.DATA,
        title: 'gnav.appSwitcher.DATA',
        privileges: [PrivilegeList.CanAccessDataTab],
        shouldRedirectToV1: true,
        pageContext: BlinkV2MetricsContextStates.DATA_MANAGEMENT,
        appModule: AppModule.Data,
    },
    [Applications.DEVELOP]: {
        children: DEVELOP_ROUTES,
        description: 'gnav.appSwitcher.description.DEVELOPER',
        element: <Outlet />,
        handle: Applications.DEVELOP,
        homePath: '/develop',
        icon: IconID.CODE,
        id: Applications.DEVELOP,
        title: 'gnav.appSwitcher.DEVELOPER',
        privileges: [PrivilegeList.HasDeveloperAccess],
        pageContext: BlinkV2MetricsContextStates.EMBED_PAGE,
        appModule: AppModule.Develop,
    },
    [Applications.ADMIN]: {
        children: ADMIN_ROUTES,
        description: 'gnav.appSwitcher.description.ADMIN',
        element: <Outlet />,
        handle: Applications.ADMIN,
        homePath: '/admin',
        icon: IconID.COMMUNITY,
        id: Applications.ADMIN,
        title: 'gnav.appSwitcher.ADMIN',
        privileges: [
            PrivilegeList.HasAdminAccess,
            PrivilegeList.IsNotEmbeddedApp,
        ],
        shouldRedirectToV1: true,
        pageContext: BlinkV2MetricsContextStates.ADMIN_PAGE,
        appModule: AppModule.Admin,
    },
    /* IMPORTANT: Do not remove this TEAMS_ADMIN route - it's actively used in all MY* clusters.
     * Unlike other admin routes, this route does not validate admin access privileges
     * because MY* clusters don't have the concept of organizations and don't grant
     * traditional admin privileges to users.
     */
    [Applications.TEAMS_ADMIN]: {
        children: TEAMS_ROUTES,
        description: 'gnav.appSwitcher.description.ADMIN',
        element: <Outlet />,
        handle: Applications.TEAMS_ADMIN,
        homePath: '/teams',
        icon: IconID.COMMUNITY,
        id: Applications.TEAMS_ADMIN,
        title: 'gnav.appSwitcher.TEAMS_ADMIN',
        privileges: [PrivilegeList.HasTeamsAccess],
        pageContext: BlinkV2MetricsContextStates.TEAMS,
    },
    [Applications.CORE]: {
        children: AUTHENTICATED_CORE_ROUTES,
        element: <Outlet />,
        handle: Applications.CORE,
        homePath: '',
        id: Applications.CORE,
        privileges: [],
        pageContext: BlinkV2MetricsContextStates.DEFAULT_PAGE,
        title: 'gnav.appSwitcher.CORE',
        appModule: AppModule.Insights,
    },
};
